"use client";

import { CategoryCard } from "@/components/CategoryCard";
import { ServiceCard } from "@/components/ServiceCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ChevronRight, Clock, Filter, Loader2, MapPin, Search, Star, Users } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

interface Service {
	id: string;
	name: string;
	description: string | null;
	is_active?: boolean;
	duration: string;
	capacity: string;
	ageLimit: string;
	category: string;
	category_id?: string | null;
	category_info?: {
		id: string;
		name: string;
		description: string | null;
		image_url: string | null;
		display_order: number;
		is_active: boolean;
	} | null;
	location: string;
	features: string[];
	image_url: string | null;
	base_price: number;
	pricing_model?: "per_participant" | "fixed_total" | "per_equipment_unit" | "per_participant_range";
	participant_pricing_ranges?: Array<{
		id: string;
		min_participants: number;
		max_participants: number;
		total_price: number;
	}>;
	schedule: string[];
	gallery: string[];
}

interface ServiceCategory {
	id: string;
	name: string;
	description: string | null;
	image_url: string | null;
	display_order: number;
	is_active: boolean;
}

const durations = ["Tous", "2h", "2.5h", "3h", "4h"];

export default function ServicesPage() {
	const [services, setServices] = useState<Service[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [categories, setCategories] = useState<ServiceCategory[]>([]);
	const [categoriesLoading, setCategoriesLoading] = useState(true);
	const [categoriesError, setCategoriesError] = useState<string | null>(null);
	const [selectedService, setSelectedService] = useState<Service | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
	const [selectedDuration, setSelectedDuration] = useState("Tous");
	const [showAllServices, setShowAllServices] = useState(false);
	const [favorites, setFavorites] = useState<string[]>([]);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const router = useRouter();
	const searchParams = useSearchParams();

	// Fetch services from API
	useEffect(() => {
		const fetchServices = async () => {
			try {
				setLoading(true);
				const response = await fetch("/api/services");
				if (!response.ok) {
					throw new Error("Impossible de charger les activités");
				}
				const data = await response.json();
				setServices(data.services);
			} catch (err) {
				setError(err instanceof Error ? err.message : "An error occurred");
			} finally {
				setLoading(false);
			}
		};

		const fetchCategories = async () => {
			try {
				setCategoriesLoading(true);
				const response = await fetch("/api/service-categories");
				if (!response.ok) {
					throw new Error("Impossible de charger les catégories");
				}
				const data = await response.json();
				setCategories(data.categories || []);
			} catch (err) {
				setCategoriesError(err instanceof Error ? err.message : "An error occurred");
			} finally {
				setCategoriesLoading(false);
			}
		};

		fetchServices();
		fetchCategories();
	}, []);

	useEffect(() => {
		const categoryParam = searchParams.get("category");
		const viewParam = searchParams.get("view");

		if (categoryParam) {
			setSelectedCategoryId(categoryParam);
			setShowAllServices(false);
		} else {
			setSelectedCategoryId(null);
			setShowAllServices(viewParam === "all");
		}
	}, [searchParams]);

	const activeServices = services.filter((service) => service.is_active !== false);

	const filteredServices = activeServices.filter((service) => {
		if (!showAllServices && !selectedCategoryId) {
			return false;
		}
		const matchesSearch =
			service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()));
		const matchesCategory = selectedCategoryId ? service.category_id === selectedCategoryId : true;
		const matchesDuration = selectedDuration === "Tous" || service.duration === selectedDuration;

		return matchesSearch && matchesCategory && matchesDuration;
	});

	const toggleFavorite = (serviceId: string) => {
		setFavorites((prev) =>
			prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
		);
	};

	const selectedCategory = selectedCategoryId
		? categories.find((category) => category.id === selectedCategoryId) || null
		: null;
	const sortedCategories = [...categories].sort((a, b) => {
		const orderDiff = (a.display_order || 0) - (b.display_order || 0);
		if (orderDiff !== 0) {
			return orderDiff;
		}
		return (a.name || "").localeCompare(b.name || "");
	});
	const showServiceList = showAllServices || Boolean(selectedCategoryId);

	return (
		<div className="min-h-screen bg-gradient-to-b from-sky-50 to-emerald-50">
			{/* Header */}
			<header className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-50">
				<div className="container mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						<Link href="/" className="flex items-center space-x-3">
							<Image
								src="/images/logo-hd.png"
								alt="Soleil & Découverte"
								width={60}
								height={60}
								className="object-contain"
							/>
							<div>
								<h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
									Soleil & Découverte
								</h1>
								<p className="text-sm text-emerald-600">L'aventure au cœur de la Guadeloupe</p>
							</div>
						</Link>

						<nav className="hidden md:flex items-center space-x-8">
							<Link
								href="/"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Accueil
							</Link>
							<Link href="/activites" className="text-emerald-600 font-semibold">
								Activités
							</Link>
							<Link
								href="/partenaires"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Partenaires
							</Link>
							<Link
								href="/contact"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Contact
							</Link>
							<Link href="/reservation">
								<Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
									Réserver
								</Button>
							</Link>
						</nav>

						{/* Mobile menu button */}
						<Button
							variant="ghost"
							className="md:hidden"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						>
							<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							</svg>
						</Button>
					</div>
				</div>

				{/* Mobile Menu */}
				{isMobileMenuOpen && (
					<div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
						<div className="container mx-auto px-4 py-4">
							<nav className="flex flex-col space-y-4">
								<Link
									href="/"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Accueil
								</Link>
								<Link
									href="/activites"
									className="text-emerald-600 font-semibold py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Activités
								</Link>
								<Link
									href="/partenaires"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Partenaires
								</Link>
								<Link
									href="/contact"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Contact
								</Link>
								<Link href="/reservation" onClick={() => setIsMobileMenuOpen(false)}>
									<Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 w-full">
										Réserver
									</Button>
								</Link>
							</nav>
						</div>
					</div>
				)}
			</header>

			{/* Hero Section */}
			<section className="relative py-20 overflow-hidden">
				<Image
					src="/images/services_hero.png"
					alt="Nos Activités"
					fill
					className="object-cover"
					style={{ zIndex: 1 }}
				/>
				<div className="absolute inset-0 bg-black/20" style={{ zIndex: 2 }}></div>

				<div className="container mx-auto px-4 relative z-10">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center max-w-4xl mx-auto"
					>
						<Badge className="mb-6 bg-white/20 text-white border-white/30 backdrop-blur-sm px-4 py-2 text-lg">
							🏝️ Nos Excursions
						</Badge>
						<h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
							Découvrez Toutes Nos
							<span className="block bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
								Activités d'Exception
							</span>
						</h1>
						<p className="text-xl text-white/90 max-w-3xl mx-auto">
							Choisissez parmi notre sélection d'excursions uniques, conçues pour vous faire vivre la
							Guadeloupe sous tous ses aspects les plus authentiques.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Filters Section */}
			{showServiceList && (
				<section className="py-8 bg-white shadow-sm">
					<div className="container mx-auto px-4">
						<div className="space-y-3">
							{selectedCategory && (
								<div className="flex items-center text-xs sm:text-sm text-gray-600 min-w-0">
									<button
										onClick={() => router.push("/activites")}
										className="hover:text-emerald-600 transition-colors"
									>
										Activités
									</button>
									<ChevronRight className="w-4 h-4 mx-2 text-gray-400 shrink-0" />
									<span className="text-emerald-600 font-semibold truncate">{selectedCategory.name}</span>
								</div>
							)}

							<div className="flex flex-col gap-3 md:flex-row md:items-center">
								<div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
									<div className="relative flex-1 min-w-0">
										<Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400" />
										<Input
											placeholder="Rechercher une excursion..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											className="h-9 text-sm min-w-0 pl-9"
										/>
									</div>

									<Select value={selectedDuration} onValueChange={setSelectedDuration}>
										<SelectTrigger className="w-24 sm:w-28 h-9 text-sm shrink-0">
											<SelectValue placeholder="Durée" />
										</SelectTrigger>
										<SelectContent>
											{durations.map((duration) => (
												<SelectItem key={duration} value={duration}>
													{duration}
												</SelectItem>
											))}
										</SelectContent>
									</Select>

									<Button
										variant="outline"
										className="flex items-center gap-2 h-9 px-3 text-xs sm:text-sm whitespace-nowrap"
									>
										<Filter className="w-4 h-4" />
										Filtres
									</Button>
								</div>

								{selectedCategory && (
									<Button
										variant="outline"
										onClick={() => router.push("/activites?view=all")}
										className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 h-9 px-3 text-xs sm:text-sm whitespace-nowrap md:ml-auto"
									>
										Voir toutes les activités
									</Button>
								)}

								{showAllServices && !selectedCategory && (
									<Button
										variant="outline"
										onClick={() => router.push("/activites")}
										className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 h-9 px-3 text-xs sm:text-sm whitespace-nowrap md:ml-auto"
									>
										Voir les catégories
									</Button>
								)}
							</div>
						</div>
					</div>
				</section>
			)}

			{/* Categories Grid */}
			{!showServiceList && (
				<section className="py-12">
					<div className="container mx-auto px-4">
						{categoriesLoading && (
							<div className="flex justify-center items-center py-20">
								<Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
								<span className="ml-2 text-emerald-600">Chargement des catégories...</span>
							</div>
						)}

						{categoriesError && (
							<div className="text-center py-20">
								<div className="text-red-600 mb-4">
									<span className="text-lg font-semibold">Erreur de chargement</span>
								</div>
								<p className="text-gray-600 mb-4">{categoriesError}</p>
							</div>
						)}

						{!categoriesLoading && !categoriesError && sortedCategories.length === 0 && (
							<div className="text-center py-20">
								<div className="text-gray-600 mb-4">
									<span className="text-lg font-semibold">Aucune catégorie disponible</span>
								</div>
								<p className="text-gray-500">Ajoutez des catégories pour organiser vos activités.</p>
							</div>
						)}

						{!categoriesLoading && !categoriesError && sortedCategories.length > 0 && (
							<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
								{sortedCategories.map((category) => {
									const serviceCount = activeServices.filter(
										(service) => service.category_id === category.id
									).length;
									return (
										<motion.button
											key={category.id}
											whileHover={{ y: -10 }}
											transition={{ duration: 0.6 }}
											onClick={() => router.push(`/activites?category=${category.id}`)}
											className="text-left h-full"
										>
											<CategoryCard
												category={category}
												serviceCount={serviceCount}
												isCountLoading={loading}
											/>
										</motion.button>
									);
								})}
							</div>
						)}

						<div className="flex justify-center mt-10">
							<Button
								onClick={() => router.push("/activites?view=all")}
								className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg"
							>
								Voir toutes les activités
							</Button>
						</div>
					</div>
				</section>
			)}

			{/* Services Grid */}
			{showServiceList && (
				<section className="py-12">
					<div className="container mx-auto px-4">
						{loading && (
							<div className="flex justify-center items-center py-20">
								<Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
								<span className="ml-2 text-emerald-600">Chargement des activités...</span>
							</div>
						)}

						{error && (
							<div className="text-center py-20">
								<div className="text-red-600 mb-4">
									<span className="text-lg font-semibold">Erreur de chargement</span>
								</div>
								<p className="text-gray-600 mb-4">{error}</p>
								<Button
									onClick={() => window.location.reload()}
									className="bg-emerald-600 hover:bg-emerald-700"
								>
									Réessayer
								</Button>
							</div>
						)}

						{!loading && !error && filteredServices.length === 0 && (
							<div className="text-center py-20">
								<div className="text-gray-600 mb-4">
									<span className="text-lg font-semibold">Aucune activité trouvée</span>
								</div>
								<p className="text-gray-500 mb-4">Essayez de modifier vos criteres de recherche.</p>
								<Button
									onClick={() => {
										setSearchTerm("");
										setSelectedDuration("Tous");
									}}
									className="bg-emerald-600 hover:bg-emerald-700"
								>
									Réinitialiser les filtres
								</Button>
							</div>
						)}

						{!loading && !error && filteredServices.length > 0 && (
							<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
								{filteredServices.map((service, index) => (
									<ServiceCard
										key={service.id}
										service={service}
										index={index}
										onDetailsClick={setSelectedService}
									/>
								))}
							</div>
						)}
					</div>
				</section>
			)}
			{/* Service Detail Modal */}
			{selectedService && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.9 }}
						className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
					>
						<div className="relative">
							<Image
								src={selectedService.image_url || "/placeholder.svg"}
								alt={selectedService.name}
								width={800}
								height={400}
								className="w-full h-64 object-cover rounded-t-2xl"
							/>
							<button
								onClick={() => setSelectedService(null)}
								className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 transition-colors"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						<div className="p-8">
							<div className="flex items-start justify-between mb-6">
								<div>
									<h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedService.name}</h2>
									<div className="flex items-center gap-4 text-sm text-gray-500">
										<Badge variant="outline" className="text-emerald-600 border-emerald-200">
											{selectedService.category}
										</Badge>
										<div className="flex items-center">
											<Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
											4.5 (12 avis)
										</div>
									</div>
								</div>
								<div className="text-right">
									<div className="text-lg font-bold text-emerald-600">{selectedService.ageLimit}</div>
									<div className="text-sm text-gray-500">Conditions d'âge</div>
								</div>
							</div>

							<p className="text-gray-700 mb-6 leading-relaxed">{selectedService.description}</p>

							<div className="grid md:grid-cols-2 gap-6 mb-6">
								<div>
									<h3 className="font-semibold text-gray-900 mb-3">Informations pratiques</h3>
									<div className="space-y-2 text-sm">
										<div className="flex items-center">
											<Clock className="w-4 h-4 mr-2 text-emerald-600" />
											Durée: {selectedService.duration}
										</div>
										<div className="flex items-center">
											<Users className="w-4 h-4 mr-2 text-emerald-600" />
											Capacité: {selectedService.capacity}
										</div>
										<div className="flex items-center">
											<MapPin className="w-4 h-4 mr-2 text-emerald-600" />
											Lieu: {selectedService.location}
										</div>
									</div>
								</div>

								<div>
									<h3 className="font-semibold text-gray-900 mb-3">Inclus dans l'excursion</h3>
									<ul className="space-y-1 text-sm">
										{selectedService.features.map((feature, idx) => (
											<li key={idx} className="flex items-center">
												<div className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></div>
												{feature}
											</li>
										))}
									</ul>
								</div>
							</div>

							<div className="mb-6">
								<h3 className="font-semibold text-gray-900 mb-3">Horaires disponibles</h3>
								<div className="flex flex-wrap gap-2">
									{selectedService.schedule.map((time, idx) => (
										<Badge key={idx} variant="outline" className="px-3 py-1">
											{time}
										</Badge>
									))}
								</div>
							</div>

							<div className="flex gap-4">
								<Button variant="outline" onClick={() => setSelectedService(null)} className="flex-1">
									Fermer
								</Button>
								<Link href={`/reservation?service=${selectedService.id}`}>
									<Button className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white">
										Réserver Maintenant
										<ChevronRight className="w-4 h-4 ml-2" />
									</Button>
								</Link>
							</div>
						</div>
					</motion.div>
				</div>
			)}

			{/* CTA Section */}
			<section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700 relative overflow-hidden">
				<div className="container mx-auto px-4 relative z-10">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center max-w-4xl mx-auto"
					>
						<h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Besoin d'Aide pour Choisir ?</h2>
						<p className="text-xl text-white/90 mb-8">
							Notre équipe est là pour vous conseiller et créer l'excursion parfaite selon vos envies !
						</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Link href="/contact">
								<Button
									size="lg"
									className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 text-lg font-semibold"
								>
									Nous Contacter
									<ChevronRight className="w-5 h-5 ml-2" />
								</Button>
							</Link>
						</div>
					</motion.div>
				</div>
			</section>

			<Footer />
		</div>
	);
}
